import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloudOutlined, RefreshOutlined, RouterOutlined, SlideshowOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import OrganizationContentStack from '@/components/organization-content-stack';
import PageHeaderBar from '@/components/common/page-header-bar';
import EmptyState from '@/components/common/empty-state';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import {
  filterMediaAgentsByStatus,
  useListMediaAgentPoolQuery,
} from '@/state/queries/media-agent-admin/use-list-media-agent-pool';
import {
  useCreateAzureMediaAgentMutation,
  useCreateStandaloneMediaAgentMutation,
} from '@/state/mutations/media-agent-admin/use-media-agent-lifecycle-mutations';
import { usePairMediaAgentDeviceMutation } from '@/state/mutations/media-agent-admin/use-pair-media-agent-device';
import { useNotifications } from '@/hooks/use-notifications';
import MediaAgentLifecycleActions, {
  MediaAgentVmCell,
} from '@/components/media-agent-admin/media-agent-lifecycle-actions';
import MediaAgentPoolConfigForm from '@/components/media-agent-admin/media-agent-pool-config-form';
import MediaAgentBookingsTable from '@/components/media-agent-admin/media-agent-bookings-table';
import AgentPairDeviceDialog from '@/components/agent-pools-admin/agent-pair-device-dialog';
import AgentPoolPlayerCell from '@/components/agent-pools-admin/agent-pool-player-cell';
import AgentPoolIdCell from '@/components/agent-pools-admin/agent-pool-id-cell';
import MediaAgentAuthStatusCell from '@/components/media-agent-admin/media-agent-auth-status-cell';
import { resolveMediaAgentStartRetryCaption } from '@/helpers/media-agent-automation.helper';
import type { MediaAgentPoolMemberDto } from '@/types/dtos/media-agent/media-agent-pool.dto';

const STATUS_FILTERS = [
  { i18nKey: 'admin.mediaAgentAdmin.status.all', fallback: 'All', value: '' },
  { i18nKey: 'admin.mediaAgentAdmin.status.connected', fallback: 'Connected', value: 'connected' },
  { i18nKey: 'admin.mediaAgentAdmin.status.offline', fallback: 'Offline', value: 'offline' },
  { i18nKey: 'admin.mediaAgentAdmin.status.allocated', fallback: 'Allocated', value: 'allocated' },
  { i18nKey: 'admin.mediaAgentAdmin.status.free', fallback: 'Free', value: 'free' },
  { i18nKey: 'admin.mediaAgentAdmin.status.maintenance', fallback: 'Maintenance', value: 'maintenance' },
  { i18nKey: 'admin.mediaAgentAdmin.status.paused', fallback: 'Paused', value: 'paused' },
  { i18nKey: 'admin.mediaAgentAdmin.status.auth', fallback: 'Auth issues', value: 'auth' },
] as const;

async function copyText(value: string) {
  await navigator.clipboard.writeText(value);
}

function PausedStatusCaption({ agent }: { agent: MediaAgentPoolMemberDto }) {
  const t = useTranslations();
  const caption = resolveMediaAgentStartRetryCaption(t, agent.vms);

  return (
    <Typography
      variant="caption"
      color={caption.severity === 'error' ? 'error.main' : 'text.secondary'}
      title={caption.detail}
    >
      {caption.text}
    </Typography>
  );
}

type MediaAgentAdminPanelProps = {
  embedded?: boolean;
  hasAccess?: boolean;
  featureLoading?: boolean;
};

export default function MediaAgentAdminPanel({
  embedded = false,
  hasAccess: hasAccessProp,
  featureLoading: featureLoadingProp,
}: MediaAgentAdminPanelProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const featureAccess = useFeatureAccess('media-agent-pool');
  const hasAccess = hasAccessProp ?? featureAccess.hasAccess;
  const featureLoading = featureLoadingProp ?? featureAccess.isLoading;
  const [statusFilter, setStatusFilter] = useState('');
  const [pairDialogPlayerId, setPairDialogPlayerId] = useState<string | null>(null);

  const {
    data: agents = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useListMediaAgentPoolQuery({
    enabled: hasAccess,
  });
  const { mutateAsync: createAzure, isPending: isCreatingAzure } = useCreateAzureMediaAgentMutation();
  const { mutateAsync: createStandalone, isPending: isCreatingStandalone } = useCreateStandaloneMediaAgentMutation();
  const { mutateAsync: pairDevice, isPending: isPairing } = usePairMediaAgentDeviceMutation();

  const filtered = useMemo(() => filterMediaAgentsByStatus(agents, statusFilter), [agents, statusFilter]);
  const isCreating = isCreatingAzure || isCreatingStandalone;

  const onCreateAzure = useCallback(async () => {
    try {
      await createAzure({});
      notification.notifySuccess(t('admin.mediaAgentAdmin.createAzureSuccess', undefined, 'Azure media agent created'));
    } catch {
      notification.notifyError(
        t('admin.mediaAgentAdmin.createAzureError', undefined, 'Failed to create Azure media agent'),
      );
    }
  }, [createAzure, notification, t]);

  const onCreateStandalone = useCallback(async () => {
    try {
      const result = await createStandalone({});
      setPairDialogPlayerId(result.playerId);
      notification.notifySuccess(
        t('admin.mediaAgentAdmin.createStandaloneSuccess', undefined, 'Standalone shell created — pair with OTP'),
      );
    } catch {
      notification.notifyError(
        t('admin.mediaAgentAdmin.createStandaloneError', undefined, 'Failed to create standalone shell'),
      );
    }
  }, [createStandalone, notification, t]);

  const pairDialogLabels = useMemo(
    () => ({
      title: t('admin.mediaAgentAdmin.pairTitle', undefined, 'Pair standalone (OTP)'),
      pairingCodeHint: t(
        'admin.mediaAgentAdmin.pairingCode',
        undefined,
        'Pairing code from Media Agent UI on the device',
      ),
      pair: t('admin.mediaAgentAdmin.pair', undefined, 'Pair device'),
      pairing: t('admin.mediaAgentAdmin.pairing', undefined, 'Pairing…'),
      cancel: t('common.cancel', undefined, 'Cancel'),
      tokenHint: t('admin.mediaAgentAdmin.tokenHint', undefined, 'Token issued (delivered to agent via socket)'),
      invalidCode: t('admin.mediaAgentAdmin.invalidCode', undefined, 'Pairing code must be 6 digits'),
      pairSuccess: t('admin.mediaAgentAdmin.pairSuccess', undefined, 'Media agent paired'),
      pairError: t('admin.mediaAgentAdmin.pairError', undefined, 'Pairing failed'),
      copied: t('common.copied', undefined, 'Copied'),
      copyToken: t('common.copyToken', undefined, 'Copy token'),
    }),
    [t],
  );

  if (featureLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasAccess) {
    const denied = (
      <Alert severity="warning" sx={{ m: embedded ? 0 : 2 }}>
        {t(
          'admin.mediaAgentAdmin.featureRequired',
          undefined,
          'Media agent pool admin requires the media-agent-pool feature.',
        )}
      </Alert>
    );
    if (embedded) return denied;
    return <OrganizationContentStack content={denied} />;
  }

  const toolbar = (
    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: embedded ? 1.5 : 0 }}>
      <Button
        size="small"
        variant="contained"
        startIcon={isCreatingAzure ? <CircularProgress size={16} color="inherit" /> : <CloudOutlined />}
        onClick={() => void onCreateAzure()}
        disabled={isCreating}
      >
        {t('admin.mediaAgentAdmin.createAzure', undefined, 'Create Azure VM')}
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={isCreatingStandalone ? <CircularProgress size={16} color="inherit" /> : <RouterOutlined />}
        onClick={() => void onCreateStandalone()}
        disabled={isCreating}
      >
        {t('admin.mediaAgentAdmin.createStandalone', undefined, 'Create standalone shell')}
      </Button>
      <Tooltip title={t('admin.mediaAgentAdmin.refresh', undefined, 'Refresh')}>
        <span>
          <IconButton onClick={() => void refetch()} disabled={isFetching} size="small">
            {isFetching ? <CircularProgress size={20} /> : <RefreshOutlined />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const content = (
    <Box
      sx={{
        height: embedded ? 'auto' : '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        pb: embedded ? 3 : 0,
      }}
    >
      {!embedded && (
        <PageHeaderBar
          mainContent={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <SlideshowOutlined sx={{ fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('admin.mediaAgentAdmin.title', undefined, 'Media agent pool')}
                </Typography>
              </Box>
              {toolbar}
            </Box>
          }
        />
      )}
      {embedded && toolbar}

      <Alert severity="info" sx={{ mt: embedded ? 0 : 2, mb: 2 }}>
        {t(
          'admin.mediaAgentAdmin.scaleHint',
          undefined,
          'Media agents are assigned from this pool when a flow goes live. Advance bookings reserve shells before show time.',
        )}
      </Alert>

      <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2, mb: 2 }}>
        <MediaAgentPoolConfigForm enabled={hasAccess} />
      </Paper>

      <MediaAgentBookingsTable enabled={hasAccess} />

      <Stack direction="row" spacing={1} sx={{ mt: 2, mb: 2 }} flexWrap="wrap" useFlexGap>
        {STATUS_FILTERS.map((f) => (
          <Chip
            key={f.value || 'all'}
            label={t(f.i18nKey, undefined, f.fallback)}
            clickable
            color={statusFilter === f.value ? 'primary' : 'default'}
            onClick={() => setStatusFilter(f.value)}
            variant={statusFilter === f.value ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('admin.mediaAgentAdmin.error', undefined, 'Failed to load pool')}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <EmptyState
          text={t(
            'admin.mediaAgentAdmin.empty',
            undefined,
            'No media agents — create an Azure VM or standalone shell to start the pool.',
          )}
        />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.mediaAgentAdmin.columns.name', undefined, 'Name')}</TableCell>
                <TableCell>{t('admin.mediaAgentAdmin.columns.status', undefined, 'Status')}</TableCell>
                <TableCell>{t('admin.mediaAgentAdmin.columns.deployment', undefined, 'Deployment')}</TableCell>
                <TableCell>{t('admin.mediaAgentAdmin.columns.allocation', undefined, 'Allocation')}</TableCell>
                <TableCell>{t('admin.mediaAgentAdmin.columns.vm', undefined, 'VM')}</TableCell>
                <TableCell align="right">{t('admin.mediaAgentAdmin.columns.actions', undefined, 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((agent: MediaAgentPoolMemberDto) => (
                <TableRow key={agent.playerId} hover>
                  <TableCell>
                    <AgentPoolPlayerCell
                      name={agent.name ?? 'Media Agent'}
                      playerId={agent.playerId}
                      pairTooltip={t('admin.mediaAgentAdmin.pair', undefined, 'Pair device')}
                      showPair={agent.deployment === 'standalone' && !agent.isConnected}
                      onCopy={() =>
                        void copyText(agent.playerId).then(() =>
                          notification.notifySuccess(t('common.copied', undefined, 'Copied')),
                        )
                      }
                      onPair={() => setPairDialogPlayerId(agent.playerId)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5} alignItems="flex-start">
                      <Chip
                        size="small"
                        label={agent.isConnected ? 'Connected' : 'Offline'}
                        color={agent.isConnected ? 'success' : 'default'}
                        variant="outlined"
                      />
                      {agent.opsStatus === 'maintenance' && (
                        <Typography variant="caption" color="warning.main">
                          {agent.scheduledMaintenance?.endsAt
                            ? t(
                                'admin.mediaAgentAdmin.maintenanceUntil',
                                {
                                  when: new Date(agent.scheduledMaintenance.endsAt).toLocaleString(),
                                },
                                `Maintenance until ${new Date(agent.scheduledMaintenance.endsAt).toLocaleString()}`,
                              )
                            : t('admin.mediaAgentAdmin.maintenanceActive', undefined, 'Maintenance')}
                        </Typography>
                      )}
                      {agent.opsStatus === 'paused' && <PausedStatusCaption agent={agent} />}
                      <MediaAgentAuthStatusCell agent={agent} />
                      {agent.opsStatus === 'active' && agent.scheduledMaintenance?.status === 'scheduled' && (
                        <Typography variant="caption" color="warning.main">
                          {t(
                            'admin.mediaAgentAdmin.scheduledMaintenance',
                            {
                              from: new Date(agent.scheduledMaintenance.startsAt).toLocaleString(),
                              to: new Date(agent.scheduledMaintenance.endsAt).toLocaleString(),
                            },
                            `Scheduled maintenance ${new Date(agent.scheduledMaintenance.startsAt).toLocaleString()} – ${new Date(agent.scheduledMaintenance.endsAt).toLocaleString()}`,
                          )}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={agent.deployment === 'azure' ? <CloudOutlined /> : <RouterOutlined />}
                      label={agent.deployment === 'azure' ? 'Azure' : 'Standalone'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {agent.allocatedFlowId ? (
                      <AgentPoolIdCell id={agent.allocatedFlowId} prefix="Flow" />
                    ) : agent.currentBookingId ? (
                      <Stack spacing={0.25}>
                        <Typography variant="caption">
                          {t('admin.mediaAgentAdmin.booked', undefined, 'Booked')}
                        </Typography>
                        <AgentPoolIdCell id={agent.currentBookingId} prefix="Booking" />
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    <MediaAgentVmCell agent={agent} />
                  </TableCell>
                  <TableCell align="right">
                    <MediaAgentLifecycleActions agent={agent} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pairDialogPlayerId && (
        <AgentPairDeviceDialog
          open
          playerId={pairDialogPlayerId}
          labels={pairDialogLabels}
          isPairing={isPairing}
          onClose={() => setPairDialogPlayerId(null)}
          onPair={pairDevice}
        />
      )}
    </Box>
  );

  if (embedded) return content;
  return <OrganizationContentStack content={content} />;
}
